/**
 * TwinkleDots — soft purple dots that fade in/out behind the character,
 * echoing the design's twinkling stadium crowd.
 */

import { useEffect, useRef } from 'react';
import { Animated, View } from 'react-native';
import { twinkleDotsStyles as styles } from './TwinkleDots.styles';

/** Stable layout (left%, top%, size, base opacity, period ms, delay ms). */
const DOTS: { left: number; top: number; size: number; opacity: number; period: number; delay: number }[] = [
  { left: 8, top: 12, size: 4, opacity: 0.35, period: 2600, delay: 0 },
  { left: 22, top: 30, size: 5, opacity: 0.5, period: 3000, delay: 300 },
  { left: 37, top: 8, size: 3, opacity: 0.25, period: 2400, delay: 700 },
  { left: 49, top: 24, size: 4, opacity: 0.4, period: 3200, delay: 200 },
  { left: 63, top: 14, size: 5, opacity: 0.45, period: 2800, delay: 900 },
  { left: 78, top: 28, size: 4, opacity: 0.3, period: 2600, delay: 500 },
  { left: 89, top: 10, size: 3, opacity: 0.35, period: 3000, delay: 1100 },
  { left: 14, top: 48, size: 5, opacity: 0.4, period: 2900, delay: 1300 },
  { left: 30, top: 56, size: 4, opacity: 0.3, period: 2500, delay: 400 },
  { left: 56, top: 52, size: 5, opacity: 0.45, period: 3100, delay: 1000 },
  { left: 71, top: 60, size: 3, opacity: 0.25, period: 2700, delay: 600 },
  { left: 84, top: 46, size: 4, opacity: 0.4, period: 2800, delay: 1500 },
  { left: 5, top: 32, size: 3, opacity: 0.3, period: 3000, delay: 800 },
  { left: 44, top: 40, size: 4, opacity: 0.35, period: 2600, delay: 1200 },
  { left: 67, top: 38, size: 3, opacity: 0.3, period: 2400, delay: 100 },
  { left: 93, top: 34, size: 4, opacity: 0.4, period: 3200, delay: 1400 },
];

export function TwinkleDots() {
  const values = useRef(DOTS.map(() => new Animated.Value(0))).current;

  useEffect(() => {
    const loops = values.map((value, i) => {
      const { period, delay } = DOTS[i];
      return Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(value, { toValue: 1, duration: period / 2, useNativeDriver: true }),
          Animated.timing(value, { toValue: 0, duration: period / 2, useNativeDriver: true }),
        ])
      );
    });
    loops.forEach((l) => l.start());
    return () => loops.forEach((l) => l.stop());
  }, [values]);

  return (
    <View style={styles.root} pointerEvents="none">
      {DOTS.map((dot, i) => (
        <Animated.View
          key={`${dot.left}-${dot.top}`}
          style={[
            styles.dot,
            {
              left: `${dot.left}%`,
              top: `${dot.top}%`,
              width: dot.size,
              height: dot.size,
              borderRadius: dot.size / 2,
              opacity: values[i].interpolate({
                inputRange: [0, 1],
                outputRange: [dot.opacity * 0.4, dot.opacity],
              }),
            },
          ]}
        />
      ))}
    </View>
  );
}
